import { useEffect, useState } from 'react';

/**
 * This hook ensures consistent heights for similar elements in the UI by identifying the highest height among them.
 *
 * We need it to maintain alignment in the layout.
 */
export const useGetHighestHeight = (
  querySelector: string,
  { cssVarName, disableStateUpdates }: { cssVarName?: string; disableStateUpdates?: boolean } = {}
) => {
  const [highestHeight, setHighestHeight] = useState<undefined | number>();

  useEffect(() => {
    const updateHighestHeight = () => {
      let height: number | undefined;

      document.querySelectorAll(querySelector).forEach((el) => {
        // @ts-expect-error the type doesn't have style on it
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const style = el.style!;

        const prevHeight = style.height;

        style.height = '';

        if (!height || el.clientHeight > height) {
          height = el.clientHeight;
        }

        style.height = prevHeight;
      });

      if (!disableStateUpdates) {
        setHighestHeight(height);
      }

      if (cssVarName) {
        document.documentElement.style.setProperty(cssVarName, `${height}px`);
      }
    };

    updateHighestHeight();

    window.addEventListener('resize', updateHighestHeight);

    return () => window.removeEventListener('resize', updateHighestHeight);
  }, [querySelector, cssVarName, disableStateUpdates]);

  return highestHeight;
};
